import { useState } from "react";
import { split } from "lodash";
import FileIcon from "./FileIcon";
import getFileType from "../../helpers/files/getFileType";
import FolderIcon from "./FolderIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { LinkAttachmentEmbed } from "../../@types/activities";

export type Link = {
  id: string;
  url: string;
  image_url?: string;
  title?: string;
  description?: string;
  loading?: boolean;
  images?: string[];
  embed?: LinkAttachmentEmbed;
};

type LinkPreviewArgs = {
  link: Link;
  className?: string;
  imageClassName?: string;
  onImageError?: () => void;
};

export default function LinkPreview({
  link,
  imageClassName,
  onImageError,
}: LinkPreviewArgs) {
  const type = split(link.id, "_")[0];
  const imageUrl = link.image_url || link.images?.[0];
  const [imageError, setImageError] = useState(false);
  const description = link.description?.trim();

  const handleImageError = () => {
    setImageError(true);
    onImageError?.();
  };

  return (
    <>
      {imageUrl && !imageError && (
        <img
          src={imageUrl}
          alt=""
          className={classNames(
            "w-16 h-16 shrink-0 object-cover object-center rounded",
            imageClassName,
          )}
          onError={handleImageError}
          onLoad={(e) => {
            // The storage image proxy answers with a 1x1 empty PNG when it
            // cannot fetch the source image (issue #4816). That "loads"
            // successfully, so onError never fires — detect it by size and
            // treat it as a missing preview image.
            const img = e.currentTarget;
            if (img.naturalWidth <= 1 || img.naturalHeight <= 1) {
              handleImageError();
            }
          }}
        />
      )}
      <div className="px-2 py-1 flex flex-col overflow-hidden grow-0">
        <div className="flex gap-2 items-center">
          {type === "file" && (
            <FileIcon
              extension={getFileType(link.title || "")}
              className="shrink-0 text-sm"
            />
          )}
          {type === "folder" && <FolderIcon className="shrink-0 text-sm" />}
          {type === "appointment" && (
            <FontAwesomeIcon className="shrink-0 text-sm" icon={faCalendar} />
          )}
          <a
            className="font-medium text-sm truncate block overflow-hidden text-ellipsis"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.title?.trim() || link.url}
          </a>
        </div>
        {description && (
          <span className="text-xs text-muted line-clamp-2 text-ellipsis overflow-hidden">
            {description}
          </span>
        )}
      </div>
    </>
  );
}
